import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useContact } from '../../hooks/useContact';
import { PageLoader } from '../ui/LoadingSpinner';
import { EmptyState } from '../ui/EmptyState';
import { Badge } from '../ui/Badge';
import { BuyerBadge } from './BuyerBadge';
import { AcquisitionProfile } from '../../types';
import { EmailThreadPanel } from './EmailThreadPanel';

interface ContactDetailProps {
  id: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function FieldRow({ label, value, masked }: { label: string; value?: string | null; masked?: boolean }) {
  return (
    <div className="detail-field">
      <span className="detail-label">{label}</span>
      {masked && value ? (
        <span className="pii-masked">●●●●●●</span>
      ) : (
        <span className={`detail-value ${!value ? 'empty' : ''}`}>
          {value ?? '—'}
        </span>
      )}
    </div>
  );
}

function ProfileCard({ profile }: { profile: AcquisitionProfile }) {
  const confidence = profile.ai_confidence;
  const confPct = confidence != null ? Math.round(confidence * 100) : null;

  return (
    <div className="profile-card">
      <div className="profile-card-header">
        <div className="flex-center gap-8">
          <span style={{ fontWeight: '500', fontSize: '13px' }}>
            {profile.profile_name ?? 'Ankaufsprofil'}
          </span>
          {!profile.is_active && <Badge variant="gray">Inaktiv</Badge>}
          {profile.source_language && (
            <Badge variant="gray">{profile.source_language.toUpperCase()}</Badge>
          )}
        </div>
        <div className="flex-center gap-8">
          {confPct != null && (
            <span style={{ fontSize: '11px', color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
              KI {confPct}%
            </span>
          )}
          {profile.source && (
            <Badge variant="blue">{profile.source}</Badge>
          )}
        </div>
      </div>
      <div className="profile-card-body">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {/* Asset */}
          <div>
            <div style={{ fontWeight: '500', color: 'var(--text)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Asset ({profile.weight_asset}%)
            </div>
            {profile.target_countries?.length ? (
              <div>{profile.target_countries.join(', ')}</div>
            ) : <span style={{ color: 'var(--text3)' }}>—</span>}
          </div>
          {/* Location */}
          <div>
            <div style={{ fontWeight: '500', color: 'var(--text)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Standort ({profile.weight_location}%)
            </div>
            {profile.target_cities?.length ? (
              <div>{profile.target_cities.slice(0, 3).join(', ')}</div>
            ) : <span style={{ color: 'var(--text3)' }}>—</span>}
          </div>
          {/* Financial */}
          <div>
            <div style={{ fontWeight: '500', color: 'var(--text)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Finanzen ({profile.weight_financial}%)
            </div>
            {(profile.volume_min_eur || profile.volume_max_eur) ? (
              <div>
                {profile.volume_min_eur
                  ? `${(profile.volume_min_eur / 1_000_000).toFixed(0)} Mio.`
                  : '—'}
                {' – '}
                {profile.volume_max_eur
                  ? `${(profile.volume_max_eur / 1_000_000).toFixed(0)} Mio. EUR`
                  : 'offen'}
              </div>
            ) : <span style={{ color: 'var(--text3)' }}>—</span>}
            {profile.risk_profile && (
              <div style={{ marginTop: '2px' }}>
                <Badge variant="amber">{profile.risk_profile}</Badge>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ContactDetail({ id }: ContactDetailProps) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useContact(id);

  if (isLoading) return <PageLoader />;

  if (isError || !data?.data) {
    return (
      <EmptyState
        icon="⚠️"
        title="Kontakt nicht gefunden"
        text="Dieser Kontakt existiert nicht oder wurde gelöscht."
        action={
          <button className="btn" onClick={() => navigate('/contacts')}>
            Zurück zur Liste
          </button>
        }
      />
    );
  }

  const contact = data.data;
  const tags = contact.tags_array?.filter(t => t.trim()) ?? [];
  const profiles = contact.profiles ?? [];

  return (
    <div>
      {/* Back button */}
      <button
        className="btn btn-sm mb-20"
        onClick={() => navigate('/contacts')}
        style={{ marginBottom: '20px' }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M8 10L4 6l4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Zurück
      </button>

      {/* Header card */}
      <div className="card mb-20" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div className="flex-between">
            <div>
              <div className="flex-center gap-8 mb-8">
                <h2 style={{ fontSize: '18px', fontWeight: '500' }}>{contact.display_name}</h2>
                {contact.is_blocked && <Badge variant="red">Gesperrt</Badge>}
                <BuyerBadge isBuyer={contact.is_buyer} />
                {contact.is_postempfaenger && <Badge variant="blue">✉ Postempfänger</Badge>}
              </div>
              {contact.company && (
                <div className="text-secondary mb-4">{contact.company}</div>
              )}
              <div className="flex-center gap-8" style={{ flexWrap: 'wrap' }}>
                {tags.map(tag => (
                  <Badge key={tag} variant="gray">{tag}</Badge>
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {contact.propstack_id && (
                <div className="text-xs text-muted mb-4">
                  Propstack #{contact.propstack_id}
                </div>
              )}
              {contact.propstack_status && (
                <Badge variant="blue">{contact.propstack_status}</Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ gap: '20px' }}>
        {/* Left column */}
        <div>
          {/* Contact info */}
          <div className="card mb-20" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Kontaktdaten</span>
            </div>
            <div className="card-body">
              <div className="detail-section">
                <div className="detail-grid">
                  <FieldRow label="Anrede" value={contact.salutation} />
                  <FieldRow label="Titel" value={contact.title} />
                  <FieldRow label="Vorname" value={contact.first_name} />
                  <FieldRow label="Nachname" value={contact.last_name} />
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-section-title">Kommunikation</div>
                <div className="detail-grid">
                  <FieldRow
                    label="E-Mail"
                    value={contact.email ?? undefined}
                    masked={!!contact.email}
                  />
                  <FieldRow label="E-Mails (alle)" value={contact.emails_all} />
                  <FieldRow
                    label="Telefon"
                    value={contact.phone ?? undefined}
                    masked={!!contact.phone}
                  />
                  <FieldRow
                    label="Mobil (privat)"
                    value={contact.mobile_private ?? undefined}
                    masked={!!contact.mobile_private}
                  />
                  <FieldRow label="Telefon (Büro)" value={contact.phone_office} />
                  <FieldRow label="Mobil (Büro)" value={contact.mobile_office} />
                </div>
              </div>
            </div>
          </div>

          {/* Propstack sync info */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Propstack Sync</span>
              <Badge variant="gray">{contact.sync_direction}</Badge>
            </div>
            <div className="card-body">
              <div className="detail-grid">
                <FieldRow label="Propstack ID" value={contact.propstack_id} />
                <FieldRow label="Status" value={contact.propstack_status} />
                <FieldRow
                  label="Synced at"
                  value={formatDate(contact.propstack_synced_at)}
                />
                <FieldRow
                  label="Erstellt am (PS)"
                  value={formatDate(contact.created_in_propstack_at)}
                />
                <FieldRow
                  label="Letzter Kontakt"
                  value={formatDate(contact.last_contact_at)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right column — profiles */}
        <div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Ankaufsprofile</span>
              <Badge variant={profiles.length > 0 ? 'blue' : 'gray'}>
                {profiles.length}
              </Badge>
            </div>
            <div className="card-body">
              {profiles.length === 0 ? (
                <EmptyState
                  icon="📋"
                  title="Keine Profile"
                  text="Noch kein Ankaufsprofil hinterlegt."
                />
              ) : (
                profiles.map(profile => (
                  <ProfileCard key={profile.id} profile={profile} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* E-Mail-Verlauf — volle Breite */}
      <div style={{ marginTop: '20px' }}>
        <EmailThreadPanel
          contactId={id}
          toEmail={typeof contact.email === 'string' && contact.email !== '●●●●●●' ? contact.email : null}
        />
      </div>
    </div>
  );
}
